import React, { useState, useEffect, useMemo, useRef, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AdminBreadcrumb from '../components/AdminBreadcrumb';
import { getDashboardStats } from '../services/dashboardService';

/**
 * Dashboard Page
 *
 * Displays statistics for:
 * - Visiting bookings (new/done/pending) by day/week/month
 * - Find by request submissions
 * - Managed properties by district with autocomplete
 *
 * Features:
 * - SessionStorage cache for instant data on navigation (via dashboardService)
 * - useTransition for smooth loading → ready transitions
 * - Staggered entrance animations for stat cards
 */
const DashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();

  const [period, setPeriod] = useState('day');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState('all');
  const [districtSearch, setDistrictSearch] = useState('');
  const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);

  // Ref to always have access to the latest period without recreating the load function
  const periodRef = useRef(period);
  periodRef.current = period;
  const loadStatsRef = useRef(null);

  // Initial load + load on period change
  useEffect(() => {
    setReady(false);
    const timer = setTimeout(() => {
      loadStatsRef.current();
    }, 100);
    return () => clearTimeout(timer);
  }, [period]);

  // Define loadStats once — it reads period through the ref
  loadStatsRef.current = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDashboardStats(periodRef.current, forceRefresh);
      setStats(data);

      startTransition(() => {
        setReady(true);
        setLoading(false);
      });
    } catch (err) {
      console.error('[Dashboard] Error loading stats:', err);
      setError(err.message || 'Failed to load dashboard data');
      setLoading(false);
    }
  };

  // Extract unique districts from properties
  const districts = useMemo(() => {
    if (!stats?.properties?.byDistrict) return [];
    return Object.keys(stats.properties.byDistrict).sort();
  }, [stats]);

  // Filtered districts for autocomplete
  const filteredDistricts = useMemo(() => {
    if (!districtSearch) return districts;
    return districts.filter((d) =>
      d.toLowerCase().includes(districtSearch.toLowerCase())
    );
  }, [districts, districtSearch]);

  // Get selected district data
  const selectedDistrictData = useMemo(() => {
    if (!stats?.properties?.byDistrict) return null;
    if (selectedDistrict === 'all') {
      return { name: t('dashboard.managedProperties.byDistrict'), count: stats.properties.total };
    }
    return {
      name: selectedDistrict,
      count: stats.properties.byDistrict[selectedDistrict] || 0,
    };
  }, [stats, selectedDistrict, t]);

  // Period options
  const periodOptions = [
    { value: 'day', label: t('dashboard.period.day') },
    { value: 'week', label: t('dashboard.period.week') },
    { value: 'month', label: t('dashboard.period.month') },
  ];

  // Stat card colors
  const statCardColors = {
    new: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      icon: 'text-blue-600',
      accent: 'bg-blue-600',
    },
    done: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      icon: 'text-emerald-600',
      accent: 'bg-emerald-600',
    },
    pending: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      icon: 'text-amber-600',
      accent: 'bg-amber-600',
    },
    request: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      icon: 'text-purple-600',
      accent: 'bg-purple-600',
    },
    property: {
      bg: 'bg-indigo-50',
      border: 'border-indigo-200',
      icon: 'text-indigo-600',
      accent: 'bg-indigo-600',
    },
  };

  const handleRefresh = () => {
    setReady(false);
    if (loadStatsRef.current) {
      loadStatsRef.current(true);
    }
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
  };

  const handleDistrictSelect = (district) => {
    setSelectedDistrict(district);
    setDistrictSearch(district);
    setShowDistrictDropdown(false);
  };

  const handleDistrictAll = () => {
    setSelectedDistrict('all');
    setDistrictSearch('');
    setShowDistrictDropdown(false);
  };

  const handleDistrictInputChange = (e) => {
    setDistrictSearch(e.target.value);
    setSelectedDistrict('all');
    setShowDistrictDropdown(true);
  };

  // --- Loading skeleton (only when we have no cached stats at all) ---
  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header Skeleton */}
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-6 animate-pulse relative overflow-hidden">
            <div className="h-8 bg-gray-200 rounded w-64 mb-2" />
            <div className="h-4 bg-gray-200 rounded w-96" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
          </div>

          {/* Statistics Grid Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className={`animate-scale-in stagger-${Math.min(i + 1, 6)} bg-white rounded-xl border border-gray-200 p-6 relative overflow-hidden`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg animate-pulse" />
                  <div className="w-16 h-4 bg-gray-200 rounded animate-pulse" />
                </div>
                <div className="h-10 w-20 bg-gray-200 rounded animate-pulse mb-2" />
                <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                <div className="h-3 w-24 bg-gray-200 rounded animate-pulse mt-2" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // --- Error state (no cached data to fall back on) ---
  if (error && !stats) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 animate-scale-in">
          <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 text-center">
            <div className="text-red-600 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('dashboard.error')}</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={handleRefresh}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              {t('dashboard.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <AdminBreadcrumb />

        {/* Header */}
        <div
          className={`bg-white rounded-xl shadow-lg p-6 md:p-8 mb-6 transition-all duration-500 ${
            ready ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('dashboard.title')}</h1>
              <p className="text-gray-600 mt-2">{t('dashboard.subtitle')}</p>
            </div>

            <div className="flex items-center gap-3">
              {/* Period Selector */}
              <div className="flex rounded-lg border border-gray-200">
                {periodOptions.map((option, i) => (
                  <button
                    key={option.value}
                    onClick={() => handlePeriodChange(option.value)}
                    className={`px-4 py-2 text-sm font-medium transition-all duration-200 ${
                      i === 0 ? 'rounded-l-lg' : i === periodOptions.length - 1 ? 'rounded-r-lg' : ''
                    } ${i > 0 ? 'border-l border-gray-200' : ''} ${
                      period === option.value
                        ? 'bg-primary-600 text-white shadow-sm'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={isPending}
                className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50"
                title={t('dashboard.refresh')}
              >
                <svg
                  className={`w-5 h-5 ${isPending ? 'animate-spin' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>

          {/* Refreshing indicator (shown during useTransition pending state) */}
          {isPending && (
            <div className="overflow-hidden transition-all duration-300 max-h-8 opacity-100 mt-3">
              <div className="flex items-center gap-2 text-sm text-primary-600">
                <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>{t('dashboard.refresh')}...</span>
              </div>
            </div>
          )}
        </div>

        {/* Statistics Grid */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6 transition-opacity duration-300 ${
            ready ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Visiting Bookings - New */}
          <button
            onClick={() => navigate('/data-grid?tab=visiting&status=new')}
            className={`${statCardColors.new.bg} ${statCardColors.new.border} border-2 rounded-xl p-6 transition-all duration-300 hover:scale-[1.03] hover:shadow-lg cursor-pointer animate-scale-in stagger-1`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`${statCardColors.new.icon} bg-white p-3 rounded-lg shadow-sm`}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">{t('dashboard.visitingBookings.new')}</span>
            </div>
            <div className="mb-2">
              <span className="text-4xl font-bold text-gray-900 tabular-nums">{stats?.visiting?.new || 0}</span>
            </div>
            <p className="text-sm text-gray-600">{t('dashboard.visitingBookings.newLabel')}</p>
            <p className="text-xs text-gray-500 mt-1">{t('dashboard.visitingBookings.newDesc')}</p>
            <div className="mt-3 text-xs font-medium text-blue-600 flex items-center gap-1">
              <span>{t('dashboard.dataGrid.visitingBookings.title')} →</span>
            </div>
          </button>

          {/* Visiting Bookings - Pending */}
          <button
            onClick={() => navigate('/data-grid?tab=visiting&status=pending')}
            className={`${statCardColors.pending.bg} ${statCardColors.pending.border} border-2 rounded-xl p-6 transition-all duration-300 hover:scale-[1.03] hover:shadow-lg cursor-pointer animate-scale-in stagger-2`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`${statCardColors.pending.icon} bg-white p-3 rounded-lg shadow-sm`}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">{t('dashboard.visitingBookings.pending')}</span>
            </div>
            <div className="mb-2">
              <span className="text-4xl font-bold text-gray-900 tabular-nums">{stats?.visiting?.pending || 0}</span>
            </div>
            <p className="text-sm text-gray-600">{t('dashboard.visitingBookings.pendingLabel')}</p>
            <p className="text-xs text-gray-500 mt-1">{t('dashboard.visitingBookings.pendingDesc')}</p>
            <div className="mt-3 text-xs font-medium text-amber-600 flex items-center gap-1">
              <span>{t('dashboard.dataGrid.visitingBookings.title')} →</span>
            </div>
          </button>

          {/* Visiting Bookings - Done */}
          <button
            onClick={() => navigate('/data-grid?tab=visiting&status=done')}
            className={`${statCardColors.done.bg} ${statCardColors.done.border} border-2 rounded-xl p-6 transition-all duration-300 hover:scale-[1.03] hover:shadow-lg cursor-pointer animate-scale-in stagger-3`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`${statCardColors.done.icon} bg-white p-3 rounded-lg shadow-sm`}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">{t('dashboard.visitingBookings.done')}</span>
            </div>
            <div className="mb-2">
              <span className="text-4xl font-bold text-gray-900 tabular-nums">{stats?.visiting?.done || 0}</span>
            </div>
            <p className="text-sm text-gray-600">{t('dashboard.visitingBookings.doneLabel')}</p>
            <p className="text-xs text-gray-500 mt-1">{t('dashboard.visitingBookings.doneDesc')}</p>
            <div className="mt-3 text-xs font-medium text-emerald-600 flex items-center gap-1">
              <span>{t('dashboard.dataGrid.visitingBookings.title')} →</span>
            </div>
          </button>

          {/* Find by Request */}
          <button
            onClick={() => navigate('/data-grid?tab=findByRequest')}
            className={`${statCardColors.request.bg} ${statCardColors.request.border} border-2 rounded-xl p-6 transition-all duration-300 hover:scale-[1.03] hover:shadow-lg cursor-pointer animate-scale-in stagger-4`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`${statCardColors.request.icon} bg-white p-3 rounded-lg shadow-sm`}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">{t('dashboard.findByRequest.title')}</span>
            </div>
            <div className="mb-2">
              <span className="text-4xl font-bold text-gray-900 tabular-nums">{stats?.findByRequest?.count || 0}</span>
            </div>
            <p className="text-sm text-gray-600">{t('dashboard.findByRequest.total')}</p>
            <p className="text-xs text-gray-500 mt-1">{t('dashboard.findByRequest.subtitle')}</p>
            <div className="mt-3 text-xs font-medium text-purple-600 flex items-center gap-1">
              <span>{t('dashboard.dataGrid.findByRequest.title')} →</span>
            </div>
          </button>

          {/* Managed Properties Total */}
          <div className={`${statCardColors.property.bg} ${statCardColors.property.border} border-2 rounded-xl p-6 transition-all duration-300 hover:scale-[1.03] hover:shadow-lg animate-scale-in stagger-5`}>
            <div className="flex items-start justify-between mb-4">
              <div className={`${statCardColors.property.icon} bg-white p-3 rounded-lg shadow-sm`}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">{t('dashboard.managedProperties.total')}</span>
            </div>
            <div className="mb-2">
              <span className="text-4xl font-bold text-gray-900 tabular-nums">{stats?.properties?.total || 0}</span>
            </div>
            <p className="text-sm text-gray-600">{t('dashboard.managedProperties.total')}</p>
            <p className="text-xs text-gray-500 mt-1">{t('dashboard.managedProperties.subtitle')}</p>
          </div>

          {/* Managed Properties by District */}
          <div className="bg-white border-2 border-gray-200 rounded-xl p-6 transition-all duration-300 hover:scale-[1.01] hover:shadow-md animate-scale-in stagger-6">
            <div className="flex items-start justify-between mb-4">
              <div className="text-indigo-600 bg-indigo-50 p-3 rounded-lg shadow-sm">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-600 uppercase tracking-wider">{t('dashboard.managedProperties.byDistrict')}</span>
            </div>

            {/* District Autocomplete */}
            <div className="mb-4 relative">
              <input
                type="text"
                value={districtSearch}
                onChange={handleDistrictInputChange}
                onFocus={() => setShowDistrictDropdown(true)}
                onBlur={() => setTimeout(() => setShowDistrictDropdown(false), 200)}
                placeholder={t('dashboard.managedProperties.byDistrict')}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
              />
              {showDistrictDropdown && filteredDistricts.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto animate-fade-in-up">
                  <button
                    onClick={handleDistrictAll}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm font-medium text-gray-900 border-b border-gray-100 transition-colors"
                  >
                    {t('dashboard.period.all')}
                  </button>
                  {filteredDistricts.map((district) => (
                    <button
                      key={district}
                      onClick={() => handleDistrictSelect(district)}
                      className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-sm text-gray-700 border-b border-gray-100 last:border-b-0 transition-colors"
                    >
                      {district}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected District Count */}
            <div className="mb-2">
              <span className="text-3xl font-bold text-gray-900 tabular-nums">{selectedDistrictData?.count || 0}</span>
            </div>
            <p className="text-sm text-gray-600 font-medium">{selectedDistrictData?.name || ''}</p>
            <p className="text-xs text-gray-500 mt-1">{t('dashboard.managedProperties.count')}</p>

            {/* District List */}
            {districts.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200 animate-fade-in-up">
                <p className="text-xs font-medium text-gray-700 mb-2">{t('dashboard.managedProperties.byDistrict')}:</p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {districts.slice(0, 10).map((district) => (
                    <div key={district} className="flex justify-between items-center text-xs hover:bg-gray-50 px-1 py-0.5 rounded transition-colors">
                      <span className="text-gray-600 truncate flex-1">{district}</span>
                      <span className="font-semibold text-gray-900 ml-2 tabular-nums">
                        {stats.properties.byDistrict[district]}
                      </span>
                    </div>
                  ))}
                  {districts.length > 10 && (
                    <p className="text-xs text-gray-500 italic">
                      +{districts.length - 10} more districts...
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Data Source Info */}
        <div
          className={`bg-white rounded-xl shadow-lg p-6 transition-all duration-500 delay-300 ${
            ready ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>📊 Data source: Google Sheets & Google Drive</span>
            </div>
            <div className="text-xs text-gray-500">
              {t('dashboard.lastUpdated')}: {new Date().toLocaleString('vi-VN')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;